import Layout from "@/components/Layout/Layout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination"
import { useStoreList } from "@/graphql/hooks/useStoreList"
import { useAllGamesWithSeasons } from "@/graphql/hooks/useGames"
import { useUrlPagination } from "@/hooks/useUrlPagination"
import {
  Phone,
  ExternalLink,
  Search,
  Filter,
  Store,
  MapPin,
  Sparkles,
  SlidersHorizontal,
} from "lucide-react"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuthContext } from "@/contexts/AuthContext"
import { useCountries, useRegions } from "@/graphql"
import { CountryFlag } from "@/components/ui/country-flag"

const randomPlaceholder = (id: number) => {
  const placeholders = [1, 2, 3] // tus imágenes disponibles
  const lastDigit = id % 10 // obtiene el último dígito
  const index = placeholders[lastDigit % placeholders.length] // mapea a 1, 2 o 3
  return `/img/placeholder-tienda${index}.png`
}

const Tiendas = () => {
  const { currentPage, setPage } = useUrlPagination()
  const { isAdmin, isAuthenticated } = useAuthContext()
  const [selectedCountry, setSelectedCountry] = useState<string>("0")
  const [selectedRegion, setSelectedRegion] = useState<string>("0")
  const [selectedGame, setSelectedGame] = useState<string>("0")
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("")
  const [inputValue, setInputValue] = useState<string>("")
  const [active, setActive] = useState<number>(1)
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false)
  const navigate = useNavigate()

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(inputValue)
    }, 2000)

    return () => clearTimeout(timer)
  }, [inputValue])

  // Update searchTerm when debouncedSearchTerm changes
  useEffect(() => {
    setSearchTerm(debouncedSearchTerm)
    // setPage(1); // Reset to first page when search changes
  }, [debouncedSearchTerm])

  const { countries, countriesMap } = useCountries()
  const { regions, regionsMap } = useRegions(Number(selectedCountry))

  const hasActiveFilters =
    selectedCountry !== "0" ||
    selectedRegion !== "0" ||
    selectedGame !== "0" ||
    searchTerm.length > 0 ||
    (isAdmin && active !== 1)

  // Filtros aplicados (sin contar la búsqueda), para el badge del botón de
  // filtros en mobile.
  const activeFilterCount =
    [selectedCountry, selectedRegion, selectedGame].filter((v) => v !== "0").length +
    (isAdmin && active !== 1 ? 1 : 0)

  const { stores, total, pages, loading, error, refetch } = useStoreList({
    filter: {
      page: currentPage,
      limit: 12,
      active,
      ...(Number(selectedCountry) > 0 && { countryId: parseInt(selectedCountry) }),
      ...(Number(selectedRegion) > 0 && { regionId: parseInt(selectedRegion) }),
      ...(Number(selectedGame) > 0 && { gameId: parseInt(selectedGame) }),
      ...(searchTerm && { search: searchTerm }),
    },
  })

  const { games } = useAllGamesWithSeasons(true, { isAdmin, isAuthenticated })

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setDebouncedSearchTerm(inputValue)
    }
  }

  // Filtros de selección (sin la búsqueda) para reutilizarlos tanto en el
  // layout de escritorio como dentro del Sheet de mobile.
  const renderFilterFields = () => (
    <>
      {/* Country Filter */}
      <div className="space-y-2">
        <label className="text-sm font-medium">País</label>
        <Select value={selectedCountry} onValueChange={setSelectedCountry}>
          <SelectTrigger>
            <SelectValue placeholder="Todos los países" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Todos los países</SelectItem>
            {countries.map((pais, key) => (
              <SelectItem key={key} value={pais.id.toString()}>
                {pais.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Region Filter */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Región</label>
        <Select value={selectedRegion} onValueChange={setSelectedRegion}>
          <SelectTrigger>
            <SelectValue placeholder="Todas las regiones" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Todas las regiones</SelectItem>
            {regions.map((region, key) => (
              <SelectItem key={key} value={region.id.toString()}>
                {region.fullName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Game Filter */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Juego</label>
        <Select value={selectedGame} onValueChange={setSelectedGame}>
          <SelectTrigger>
            <SelectValue placeholder="Todos los juegos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Todos los juegos</SelectItem>
            {games.map((juego, key) => (
              <SelectItem key={key} value={juego.id.toString()}>
                {juego.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tiendas activas Filter */}
      {isAdmin && (
        <div className="space-y-2">
          <label className="text-sm font-medium">Tiendas activas</label>
          <Select value={active.toString()} onValueChange={(e) => setActive(Number(e))}>
            <SelectTrigger>
              <SelectValue placeholder="Estado tiendas" />
            </SelectTrigger>
            <SelectContent>
              {[
                { id: 1, name: "Activa" },
                { id: 0, name: "Inactivas" },
              ].map((state, key) => (
                <SelectItem key={key} value={state.id.toString()}>
                  {state.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </>
  )

  const handleClearFilters = () => {
    setSelectedCountry("0")
    setSelectedRegion("0")
    setSelectedGame("0")
    setSearchTerm("")
    setInputValue("")
    setDebouncedSearchTerm("")
    setPage(1)
    setActive(1)
  }

  if (loading) {
    return (
      <Layout>
        <LoadingScreen message="Cargando tiendas..." />
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-destructive">Error al cargar las tiendas</p>
          <Button onClick={() => refetch()} className="mt-4">
            Reintentar
          </Button>
        </div>
      </Layout>
    )
  }
  return (
    <Layout>
      <div className="min-h-screen space-y-4 py-4 md:py-6">
        <section className="relative overflow-hidden rounded-2xl border border-primary/15 bg-card/55 px-5 py-6 shadow-[0_10px_45px_rgba(2,6,23,0.08)] backdrop-blur-xl md:px-8 md:py-8">
          <div className="absolute -left-20 -top-14 h-44 w-44 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -right-20 top-10 h-56 w-56 rounded-full bg-cyan-500/10 blur-3xl" />

          <div className="relative flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-2">
              <Badge className="inline-flex gap-2 border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                Tiendas Oficiales MyL
              </Badge>

              <h1 className="text-3xl font-black tracking-tight md:text-5xl">
                Tiendas
                <span className="block bg-gradient-to-r from-primary via-cyan-400 to-primary bg-[length:200%_200%] bg-clip-text text-transparent animate-gradient">
                  Asociadas
                </span>
              </h1>

              <p className="max-w-3xl text-base text-muted-foreground md:text-lg">
                Encuentra tiendas para jugar, competir y participar en torneos oficiales de Mitos y
                Leyendas.
              </p>
            </div>
          </div>
        </section>

        {/* Filters */}
        <Card className="border-border/70 bg-card/70 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                <Filter className="w-4 h-4 shrink-0 sm:w-5 sm:h-5" />
                <span className="truncate">Filtros de Búsqueda</span>
              </CardTitle>

              {/* Mobile: abre el resto de los filtros en un sidebar */}
              <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 md:hidden">
                    <SlidersHorizontal className="w-4 h-4" />
                    Filtros
                    {activeFilterCount > 0 && (
                      <Badge variant="secondary" className="h-5 px-1.5">
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-5/6 overflow-y-auto sm:max-w-sm">
                  <SheetHeader>
                    <SheetTitle>Filtros</SheetTitle>
                  </SheetHeader>
                  <div className="grid grid-cols-1 gap-4 mt-4">{renderFilterFields()}</div>
                  <div className="flex justify-between gap-2 pt-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!hasActiveFilters}
                      onClick={handleClearFilters}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Limpiar filtros
                    </Button>
                    <Button size="sm" onClick={() => setIsFilterSheetOpen(false)}>
                      Ver resultados
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
            <p className="text-sm text-muted-foreground">Se encontraron {total} registros.</p>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Search - siempre visible */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Nombre de tienda..."
                  value={inputValue}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyPress}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Resto de filtros - solo en desktop, en mobile van en el Sheet de arriba */}
            <div
              className={`hidden md:grid gap-4 md:grid-cols-2 ${isAdmin ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}
            >
              {renderFilterFields()}
            </div>

            {/* Clear Filters - solo desktop */}
            <div className="hidden md:flex justify-end">
              <Button
                variant="outline"
                className="min-w-40"
                disabled={!hasActiveFilters}
                onClick={handleClearFilters}
              >
                Limpiar Filtros
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stores Grid */}
        {stores.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {stores.map((store) => (
              <Card
                key={store.id}
                className="group overflow-hidden border-border/60 bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-primary/30"
              >
                {/* Store Cover Image */}
                <div
                  className="relative cursor-pointer overflow-hidden"
                  onClick={() => navigate(store.urlView)}
                >
                  <img
                    src={store.urlCover || randomPlaceholder(store.id)}
                    alt={store.name}
                    className="h-44 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                  {/* Level badge overlaid on image */}
                  {store.Level && (
                    <div className="absolute top-3 left-3">
                      <Badge className="bg-black/50 backdrop-blur-sm border-white/20 text-white flex items-center gap-1.5 px-2 py-1">
                        {store.Level.icon && (
                          <img
                            src={store.Level.icon}
                            alt={store.Level.name}
                            className="w-3.5 h-3.5 object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = "none"
                            }}
                          />
                        )}
                        <span className="text-[11px] font-medium">{store.Level.name}</span>
                      </Badge>
                    </div>
                  )}

                  <div className="absolute bottom-3 right-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <div className="rounded-full bg-white/20 backdrop-blur-sm p-1.5">
                      <ExternalLink className="h-3.5 w-3.5 text-white" />
                    </div>
                  </div>

                  {/* Store name overlaid at bottom of image */}
                  <div className="absolute bottom-0 left-0 right-0 px-4 pb-3">
                    <h3 className="text-base font-bold text-white leading-tight drop-shadow-sm line-clamp-1">
                      {store.name}
                    </h3>
                  </div>
                </div>

                <CardContent className="p-4 space-y-3">
                  {store.description && (
                    <p className="line-clamp-2 text-xs text-muted-foreground leading-relaxed">
                      {store.description}
                    </p>
                  )}

                  <div className="space-y-1.5">
                    {store.Address && (
                      <div className="flex items-start gap-2 text-xs text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5 shrink-0 text-primary mt-0.5" />
                        <CountryFlag
                          countryCode={countriesMap.get(store.Address?.countryId)?.iso}
                          size=""
                        />
                        <span className="line-clamp-2">
                          {store.Address.address}
                          {store.Address.Location && `, ${store.Address.Location.name}`}
                          {`, ${regionsMap?.get(store.Address?.regionId)?.fullName}`}
                        </span>
                      </div>
                    )}

                    {store.phone && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Phone className="w-3.5 h-3.5 shrink-0 text-primary" />
                        <span>{store.phone}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 pt-1">
                    {store.urlView && (
                      <Button
                        className="flex-1 h-8 text-xs"
                        size="sm"
                        onClick={() => navigate(store.urlView)}
                      >
                        Ver Detalles
                      </Button>
                    )}
                    {store.urlTournaments && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(store.urlTournaments)}
                        className="flex-1 h-8 text-xs"
                      >
                        Torneos
                      </Button>
                    )}
                    <LinkExternal link={store.siteUrl} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-card/50 py-12 text-center">
            <Store className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-lg text-muted-foreground">
              No se encontraron tiendas con los filtros seleccionados
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => {
                setSelectedCountry("0")
                setSelectedRegion("0")
                setSelectedGame("0")
                setSearchTerm("")
                setInputValue("")
                setDebouncedSearchTerm("")
                setPage(1)
              }}
            >
              Limpiar filtros
            </Button>
          </div>
        )}

        {/* Pagination */}
        <RenderPagination pages={pages} currentPage={currentPage} setPage={setPage} />
      </div>
    </Layout>
  )
}

export default Tiendas

export const LinkExternal = ({ link = "" }) => {
  try {
    new URL(link)
  } catch {
    return <></>
  }
  return (
    <Button variant="ghost" size="sm" onClick={() => window.open(link, "_blank")} className="px-3">
      <ExternalLink className="w-4 h-4" />
    </Button>
  )
}

/**
 * Paginador Anterior/Siguiente sin números de página ni total: se usa donde
 * el backend no expone el total real de resultados (ver TournamentListV2),
 * a propósito, para no revelarle un total inferible a un scraper.
 */
export const RenderSimplePagination = ({ hasNextPage, currentPage, setPage }) => {
  if (currentPage <= 1 && !hasNextPage) return null

  return (
    <Pagination className="mt-8">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => currentPage > 1 && setPage(currentPage - 1)}
            className={`cursor-pointer ${currentPage <= 1 ? "pointer-events-none opacity-50" : ""}`}
          >
            Anterior
          </PaginationPrevious>
        </PaginationItem>

        <PaginationItem>
          <PaginationLink isActive className="pointer-events-none">
            {currentPage}
          </PaginationLink>
        </PaginationItem>

        <PaginationItem>
          <PaginationNext
            onClick={() => hasNextPage && setPage(currentPage + 1)}
            className={`cursor-pointer ${!hasNextPage ? "pointer-events-none opacity-50" : ""}`}
          >
            Siguiente
          </PaginationNext>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}

export const RenderPagination = ({ pages, currentPage, setPage }) => {
  if (pages <= 1) return null

  const renderPageNumbers = () => {
    const items = []
    const showEllipsis = pages > 7

    if (!showEllipsis) {
      for (let i = 1; i <= pages; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              onClick={() => setPage(i)}
              isActive={currentPage === i}
              className="cursor-pointer"
            >
              {i}
            </PaginationLink>
          </PaginationItem>,
        )
      }
    } else {
      // Always show first page
      items.push(
        <PaginationItem key={1}>
          <PaginationLink
            onClick={() => setPage(1)}
            isActive={currentPage === 1}
            className="cursor-pointer"
          >
            1
          </PaginationLink>
        </PaginationItem>,
      )

      // Show ellipsis if current page is far from start
      if (currentPage > 4) {
        items.push(
          <PaginationItem key="ellipsis-start">
            <PaginationEllipsis />
          </PaginationItem>,
        )
      }

      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1)
      const endPage = Math.min(pages - 1, currentPage + 1)

      for (let i = startPage; i <= endPage; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              onClick={() => setPage(i)}
              isActive={currentPage === i}
              className="cursor-pointer"
            >
              {i}
            </PaginationLink>
          </PaginationItem>,
        )
      }

      // Show ellipsis if current page is far from end
      if (currentPage < pages - 3) {
        items.push(
          <PaginationItem key="ellipsis-end">
            <PaginationEllipsis />
          </PaginationItem>,
        )
      }

      // Always show last page
      if (pages > 1) {
        items.push(
          <PaginationItem key={pages}>
            <PaginationLink
              onClick={() => setPage(pages)}
              isActive={currentPage === pages}
              className="cursor-pointer"
            >
              {pages}
            </PaginationLink>
          </PaginationItem>,
        )
      }
    }

    return items
  }

  return (
    <Pagination className="mt-8">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => currentPage > 1 && setPage(currentPage - 1)}
            className={`cursor-pointer ${currentPage <= 1 ? "pointer-events-none opacity-50" : ""}`}
          >
            Anterior
          </PaginationPrevious>
        </PaginationItem>

        {renderPageNumbers()}

        <PaginationItem>
          <PaginationNext
            onClick={() => currentPage < pages && setPage(currentPage + 1)}
            className={`cursor-pointer ${currentPage >= pages ? "pointer-events-none opacity-50" : ""}`}
          >
            Siguiente
          </PaginationNext>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}
